import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { useUserContext } from '@/lib/auth/useUserContext';
import { useViewScope } from '@/lib/scope/ViewScope';
import { formatCents, formatShortDate } from '@/lib/format';

interface Item {
  sku: string;
  description: string;
  qty: number;
  unitCostCents: number;
}

interface InventoryRequest {
  id: string;
  storeId: string;
  amountEstimateCents: number;
  notes: string | null;
  status: 'submitted' | 'approved' | 'rejected';
  requestedById: string;
  requestedByTier: 'manager' | 'store_ops';
  decidedById: string | null;
  decidedAt: string | null;
  decisionNotes: string | null;
  createdAt: string;
  items: Item[];
}

interface ListResponse {
  requests: InventoryRequest[];
}

export function InventoryRequests() {
  const { scopedStores, principal } = useUserContext();
  const { storeLabel, inScope } = useViewScope();
  const [rows, setRows] = useState<InventoryRequest[]>([]);
  const visibleRows = rows.filter((r) => inScope(r.storeId));
  const [filter, setFilter] = useState<'all' | 'submitted' | 'approved' | 'rejected'>('submitted');
  const [createOpen, setCreateOpen] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState<InventoryRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    const p = new URLSearchParams();
    if (filter !== 'all') p.set('status', filter);
    apiFetch<ListResponse>(`/inventory-requests?${p.toString()}`)
      .then((r) => setRows(r.requests))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="min-h-screen p-6 max-w-5xl mx-auto">
      <header className="flex items-center justify-between border-b border-border pb-4 mb-4">
        <h1 className="text-2xl font-semibold">Inventory requests</h1>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
        >
          New request
        </button>
      </header>

      <div className="flex items-center gap-3 mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="border border-border rounded-md px-2 py-1 text-sm"
        >
          <option value="all">all</option>
          <option value="submitted">submitted</option>
          <option value="approved">approved</option>
          <option value="rejected">rejected</option>
        </select>
      </div>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {loading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : null}

      <ul className="space-y-3">
        {visibleRows.map((r) => {
          const canDecide = r.status === 'submitted' && r.requestedById !== principal?.userId;
          return (
            <li key={r.id} className="border border-border rounded-md p-3 bg-card">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-medium">{formatCents(r.amountEstimateCents)}</div>
                  <div className="text-xs text-muted-foreground">
                    {storeLabel(r.storeId)} · {r.items.length} item
                    {r.items.length === 1 ? '' : 's'} · {r.requestedByTier} ·{' '}
                    {formatShortDate(r.createdAt)}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={
                      r.status === 'approved'
                        ? 'text-emerald-700'
                        : r.status === 'rejected'
                          ? 'text-destructive'
                          : 'text-amber-700'
                    }
                  >
                    {r.status}
                  </span>
                  {canDecide ? (
                    <button
                      type="button"
                      onClick={() => setDecisionTarget(r)}
                      className="text-xs underline underline-offset-4"
                    >
                      Decide
                    </button>
                  ) : null}
                </div>
              </div>
              <ul className="text-xs text-muted-foreground space-y-0.5">
                {r.items.slice(0, 5).map((it, idx) => (
                  <li key={idx}>
                    {it.qty}× {it.description} <code>({it.sku})</code> @{' '}
                    {formatCents(it.unitCostCents)}
                  </li>
                ))}
                {r.items.length > 5 ? <li>… and {r.items.length - 5} more</li> : null}
              </ul>
              {r.notes ? <p className="text-xs mt-2 text-muted-foreground">{r.notes}</p> : null}
            </li>
          );
        })}
        {!loading && visibleRows.length === 0 ? (
          <li className="py-6 text-center text-muted-foreground">
            No requests{rows.length > 0 ? ' in the current view scope' : ''}.
          </li>
        ) : null}
      </ul>

      {createOpen ? (
        <CreateDialog
          scopedStores={scopedStores.filter(inScope)}
          storeLabel={storeLabel}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      ) : null}
      {decisionTarget ? (
        <DecisionDialog
          request={decisionTarget}
          onClose={() => setDecisionTarget(null)}
          onDone={() => {
            setDecisionTarget(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateDialog({
  scopedStores,
  storeLabel,
  onClose,
  onCreated,
}: {
  scopedStores: string[];
  storeLabel: (id: string) => string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [storeId, setStoreId] = useState(scopedStores[0] ?? '');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<Item[]>([
    { sku: '', description: '', qty: 1, unitCostCents: 0 },
  ]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function setItem(idx: number, patch: Partial<Item>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function addRow() {
    setItems((prev) => [...prev, { sku: '', description: '', qty: 1, unitCostCents: 0 }]);
  }
  function removeRow(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }
  const total = items.reduce((s, it) => s + it.qty * it.unitCostCents, 0);

  async function submit() {
    if (items.some((it) => !it.sku || !it.description || it.qty <= 0 || it.unitCostCents < 0)) {
      setErr('every line needs sku, description, qty, and a non-negative unit cost');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiFetch('/inventory-requests', {
        method: 'POST',
        body: JSON.stringify({ storeId, notes: notes || undefined, items }),
      });
      onCreated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'submit failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-2xl space-y-3 my-8">
        <h3 className="text-lg font-semibold">New inventory request</h3>
        <label className="block text-sm">
          Store
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1 text-sm"
          >
            {scopedStores.map((s) => (
              <option key={s} value={s}>
                {storeLabel(s)}
              </option>
            ))}
          </select>
        </label>

        <div className="space-y-2">
          <div className="text-sm">Line items</div>
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2">
              <input
                placeholder="SKU"
                value={it.sku}
                onChange={(e) => setItem(idx, { sku: e.target.value })}
                className="col-span-2 border border-border rounded-md px-2 py-1 text-sm"
              />
              <input
                placeholder="Description"
                value={it.description}
                onChange={(e) => setItem(idx, { description: e.target.value })}
                className="col-span-5 border border-border rounded-md px-2 py-1 text-sm"
              />
              <input
                type="number"
                min={1}
                value={it.qty}
                onChange={(e) => setItem(idx, { qty: parseInt(e.target.value || '0', 10) })}
                className="col-span-1 border border-border rounded-md px-2 py-1 text-sm"
              />
              <input
                type="number"
                step="0.01"
                placeholder="$"
                value={(it.unitCostCents / 100).toString()}
                onChange={(e) =>
                  setItem(idx, {
                    unitCostCents: Math.round(parseFloat(e.target.value || '0') * 100),
                  })
                }
                className="col-span-3 border border-border rounded-md px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={() => removeRow(idx)}
                disabled={items.length === 1}
                className="col-span-1 text-xs text-destructive disabled:opacity-40"
              >
                ×
              </button>
            </div>
          ))}
          <button type="button" onClick={addRow} className="text-xs underline underline-offset-4">
            + Add row
          </button>
        </div>

        <label className="block text-sm">
          Notes
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1 min-h-[60px]"
          />
        </label>

        <p className="text-sm text-right">
          Total estimate: <strong>{formatCents(total)}</strong>
        </p>

        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !storeId || items.length === 0}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DecisionDialog({
  request,
  onClose,
  onDone,
}: {
  request: InventoryRequest;
  onClose: () => void;
  onDone: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function decide(decision: 'approve' | 'reject') {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/inventory-requests/${request.id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ decision, notes: notes || undefined }),
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'decision failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3">
        <h3 className="text-lg font-semibold">Decide inventory request</h3>
        <p className="text-sm">
          <strong>{formatCents(request.amountEstimateCents)}</strong> · {request.items.length} line
          {request.items.length === 1 ? '' : 's'}
        </p>
        <ul className="text-xs text-muted-foreground max-h-32 overflow-y-auto">
          {request.items.map((it, i) => (
            <li key={i}>
              {it.qty}× {it.description} @ {formatCents(it.unitCostCents)}
            </li>
          ))}
        </ul>
        <label className="block text-sm">
          Notes (optional)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1 min-h-[60px]"
          />
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void decide('reject')}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-destructive text-white text-sm disabled:opacity-60"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => void decide('approve')}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-emerald-700 text-white text-sm disabled:opacity-60"
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
